import { type InputHTMLAttributes, forwardRef, memo, useMemo } from 'react'

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string
  error?: string
  helperText?: string
  placeholder?: string
  leftIcon?: React.ReactNode
  rightIcon?: React.ReactNode
  isRequired?: boolean
}

const Input = memo(forwardRef<HTMLInputElement, InputProps>(({
  label,
  error,
  helperText,
  leftIcon,
  rightIcon,
  isRequired = false,
  className = '',
  id,
  placeholder,
  ...props
}, ref) => {
  // Memoized unique ID if not provided
  const inputId = useMemo(() => id || `input-${Math.random().toString(36).substr(2, 9)}`, [id])

  // Memoized base input classes with better accessibility and responsive design
  const baseInputClasses = useMemo(() => `
    block w-full rounded-lg border-0 py-3 px-3 text-gray-900 shadow-sm 
    ring-1 ring-inset placeholder:text-text-secondary
    transition-all duration-200 ease-in-out
    text-sm sm:text-sm leading-6
    min-h-[44px] sm:min-h-[40px]
    high-contrast:ring-1 high-contrast:ring-gray-300
  `.replace(/\s+/g, ' ').trim(), [])

  // Memoized state-based classes with better contrast
  const stateClasses = useMemo(() => error
    ? `ring-red-300 focus:ring-red-500 bg-red-50 high-contrast:ring-red-600 high-contrast:bg-red-100`
    : `ring-gray-300 focus:ring-indigo-500 hover:ring-gray-400 high-contrast:ring-gray-600`, [error])

  // Memoized icon padding classes
  const iconPaddingClasses = useMemo(() => leftIcon ? 'pl-10' : rightIcon ? 'pr-10' : '', [leftIcon, rightIcon])

  // Memoized combined input classes
  const inputClasses = useMemo(() => `${baseInputClasses} ${stateClasses} ${iconPaddingClasses} ${className}`, 
    [baseInputClasses, stateClasses, iconPaddingClasses, className])

  // Memoized aria-describedby
  const ariaDescribedBy = useMemo(() => {
    const ids = [
      error ? `${inputId}-error` : null,
      helperText ? `${inputId}-helper` : null
    ].filter(Boolean)
    return ids.length > 0 ? ids.join(' ') : undefined
  }, [error, helperText, inputId])

  return (
    <div className="w-full">
      {/* Enhanced label with better accessibility */}
      {label && (
        <label 
          htmlFor={inputId}
          className="block font-medium text-gray-700 mb-2 high-contrast:text-gray-900"
        >
          {label}
          {isRequired && (
            <span 
              className="text-red-500 ml-1 high-contrast:text-red-700" 
              aria-label="required"
            >
              *
            </span>
          )}
        </label>
      )}

      {/* Input container */}
      <div className="relative">
        {/* Enhanced left icon with better accessibility */}
        {leftIcon && (
          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
            <span className="text-text-secondary sm:text-sm high-contrast:text-gray-600" aria-hidden="true">
              {leftIcon}
            </span>
          </div>
        )}

        {/* Enhanced input field with better accessibility */}
        <input
          ref={ref}
          id={inputId}
          className={`${inputClasses}`}
          placeholder={placeholder}
          aria-invalid={error ? 'true' : 'false'}
          aria-required={isRequired}
          aria-describedby={ariaDescribedBy}
          {...props}
        />

        {/* Enhanced right icon with better accessibility */}
        {rightIcon && (
          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
            <span className="text-gray-400 sm:text-sm high-contrast:text-gray-600" aria-hidden="true">
              {rightIcon}
            </span>
          </div>
        )}
      </div>

      {/* Enhanced error message with better accessibility */}
      {error && (
        <p 
          id={`${inputId}-error`}
          className="mt-2 text-sm text-red-600 high-contrast:text-red-700 flex items-start"
          role="alert"
          aria-live="polite"
        >
          <svg 
            className="w-4 h-4 mr-1 mt-0.5 flex-shrink-0" 
            fill="currentColor" 
            viewBox="0 0 20 20"
            aria-hidden="true"
          >
            <path 
              fillRule="evenodd" 
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" 
              clipRule="evenodd" 
            />
          </svg>
          {error}
        </p>
      )}

      {/* Enhanced helper text with better accessibility */}
      {helperText && !error && (
        <p 
          id={`${inputId}-helper`}
          className="mt-2 text-sm text-text-secondary high-contrast:text-gray-700"
        >
          {helperText}
        </p>
      )}
    </div>
  )
}))

Input.displayName = 'Input'

export default Input
export { Input }